<script>
import Layout from '@/Layouts/Authenticated.vue';
import Pagination from '@/Components/Pagination.vue';
import SearchFilter from '@/Components/SearchFilter.vue';
import { Head, router } from '@inertiajs/vue3';
import { pickBy, throttle } from 'lodash';

export default {
    layout: Layout,
    components: {
        Head,
        Pagination,
        SearchFilter,
    },
    props: {
        activity_logs: {
            type: Object,
            required: true,
        },
        users: {
            type: Array,
            default: () => [],
        },
        entity_type_options: {
            type: Array,
            default: () => [],
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
        default_entity_type: {
            type: String,
            default: 'Data Retention',
        },
    },
    data() {
        return {
            expandedRows: {},
            form: {
                search: this.filters.search ?? '',
                entity_type: this.filters.entity_type ?? this.default_entity_type,
                user_id: this.filters.user_id ? String(this.filters.user_id) : '',
                date_from: this.filters.date_from ?? '',
                date_to: this.filters.date_to ?? '',
            },
        };
    },
    computed: {
        logs() {
            return this.activity_logs?.data ?? [];
        },
        userOptions() {
            return this.users
                .map((user) => ({
                    id: String(user.id),
                    name: `${user.first_name} ${user.last_name}`.trim(),
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        hasActiveFilters() {
            return Boolean(
                this.normalizedText(this.form.search)
                || (this.form.entity_type && this.form.entity_type !== this.default_entity_type)
                || this.form.user_id
                || this.form.date_from
                || this.form.date_to
            );
        },
    },
    watch: {
        'form.search': {
            handler: throttle(function () {
                this.applyFilters();
            }, 300),
        },
        'form.entity_type'() {
            this.applyFilters();
        },
        'form.user_id'() {
            this.applyFilters();
        },
        'form.date_from'() {
            this.applyFilters();
        },
        'form.date_to'() {
            this.applyFilters();
        },
    },
    methods: {
        normalizedText(value) {
            return String(value ?? '').trim();
        },
        applyFilters() {
            const query = pickBy({
                search: this.normalizedText(this.form.search) || undefined,
                user_id: this.form.user_id || undefined,
                date_from: this.form.date_from || undefined,
                date_to: this.form.date_to || undefined,
            });

            query.entity_type = this.form.entity_type;

            router.get(route('admin.change-logs.index'), query, {
                preserveState: true,
                replace: true,
                preserveScroll: true,
            });
        },
        resetFilters() {
            this.form = {
                search: '',
                entity_type: this.default_entity_type,
                user_id: '',
                date_from: '',
                date_to: '',
            };
            this.applyFilters();
        },
        getEventLabel(log) {
            return log.description || log.event || 'Updated';
        },
        getUserLabel(log) {
            if (log.log_name === 'data_retention') {
                return 'System';
            }

            const firstName = log?.user?.first_name ?? '';
            const lastName = log?.user?.last_name ?? '';
            const fullName = `${firstName} ${lastName}`.trim();

            return fullName || 'N/A';
        },
        getSubjectLabel(log) {
            return log.title || `${log.entity_type} #${log.subject_id}`;
        },
        formatDateTime(dateStr) {
            const date = new Date(dateStr);

            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        formatChangeValue(value) {
            if (value === null || value === undefined || value === '') {
                return 'N/A';
            }

            if (typeof value === 'object') {
                return JSON.stringify(value);
            }

            return String(value);
        },
        getChangesSummary(log) {
            const changes = log.changes ?? [];

            if (changes.length === 0) {
                return 'No field changes recorded';
            }

            if (changes.length === 1) {
                const change = changes[0];
                return `${change.label}: ${this.formatChangeValue(change.old_value)} → ${this.formatChangeValue(change.new_value)}`;
            }

            return `${changes.length} changes`;
        },
        toggleRow(logId) {
            this.expandedRows[logId] = !this.expandedRows[logId];
        },
        isExpanded(logId) {
            return Boolean(this.expandedRows[logId]);
        },
    },
};
</script>

<template>
    <div>
        <Head title="Change Logs" />

        <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Change Logs
        </h1>

        <div class="bg-white py-6 px-4 mt-4 sm:p-6 shadow">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 flex-1">
                <div>
                    <label for="entityType" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Item Type</label>
                    <select
                        id="entityType"
                        v-model="form.entity_type"
                        class="block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm text-gray-700 focus:border-light-pitch-purple focus:outline-none focus:ring-1 focus:ring-light-pitch-purple"
                    >
                        <option value="all">All types</option>
                        <option v-for="type in entity_type_options" :key="type" :value="type">
                            {{ type }}
                        </option>
                    </select>
                </div>

                <div>
                    <label for="userFilter" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">User</label>
                    <select
                        id="userFilter"
                        v-model="form.user_id"
                        class="block w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm text-gray-700 focus:border-light-pitch-purple focus:outline-none focus:ring-1 focus:ring-light-pitch-purple"
                    >
                        <option value="">All users</option>
                        <option v-for="user in userOptions" :key="user.id" :value="user.id">
                            {{ user.name }}
                        </option>
                    </select>
                </div>

                <div>
                    <label for="dateFrom" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">From Date</label>
                    <input
                        id="dateFrom"
                        v-model="form.date_from"
                        type="date"
                        class="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-700 focus:border-light-pitch-purple focus:outline-none focus:ring-1 focus:ring-light-pitch-purple"
                    />
                </div>

                <div>
                    <label for="dateTo" class="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">To Date</label>
                    <input
                        id="dateTo"
                        v-model="form.date_to"
                        type="date"
                        class="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-700 focus:border-light-pitch-purple focus:outline-none focus:ring-1 focus:ring-light-pitch-purple"
                    />
                </div>
            </div>

            <div class="flex items-center gap-3">
                <SearchFilter
                    v-model:value="form.search"
                    :show-filter="false"
                    class="w-full lg:w-80"
                    @reset="resetFilters"
                />
            </div>
        </div>

        <div class="flex flex-col mt-6 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Event
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subject
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Changes
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <template v-if="logs.length > 0">
                                    <template v-for="log in logs" :key="log.id">
                                        <tr class="hover:bg-gray-50">
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {{ formatDateTime(log.created_at) }}
                                            </td>
                                            <td class="px-6 py-4 text-sm text-gray-900">
                                                <span class="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                                    {{ getEventLabel(log) }}
                                                </span>
                                            </td>
                                            <td class="px-6 py-4 text-sm text-gray-900">
                                                <div class="font-medium">{{ getSubjectLabel(log) }}</div>
                                                <div class="text-xs text-gray-500">{{ log.entity_type }}</div>
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {{ getUserLabel(log) }}
                                            </td>
                                            <td class="px-6 py-4 text-sm text-gray-700">
                                                <button
                                                    v-if="(log.changes ?? []).length > 0"
                                                    type="button"
                                                    class="text-left text-indigo-600 hover:text-indigo-800"
                                                    @click="toggleRow(log.id)"
                                                >
                                                    {{ getChangesSummary(log) }}
                                                </button>
                                                <span v-else class="text-gray-400 italic">
                                                    {{ getChangesSummary(log) }}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr v-if="isExpanded(log.id)" class="bg-gray-50">
                                            <td colspan="5" class="px-6 py-4">
                                                <table class="min-w-full text-sm">
                                                    <thead>
                                                        <tr class="text-xs font-medium uppercase tracking-wide text-gray-400">
                                                            <th class="pb-2 text-left w-1/3">Field</th>
                                                            <th class="pb-2 text-left w-1/3">Previous</th>
                                                            <th class="pb-2 text-left w-1/3">Updated To</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-100">
                                                        <tr v-for="(change, index) in log.changes" :key="`${log.id}-${change.field}-${index}`">
                                                            <td class="py-2 pr-4 font-medium text-gray-700">
                                                                {{ change.label }}
                                                            </td>
                                                            <td class="py-2 pr-4 text-red-600">
                                                                {{ formatChangeValue(change.old_value) }}
                                                            </td>
                                                            <td class="py-2 text-emerald-700">
                                                                {{ formatChangeValue(change.new_value) }}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                                <tr v-else>
                                    <td colspan="5" class="px-6 py-8 text-center text-sm text-gray-500">
                                        No changelog entries found for your search criteria.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <Pagination v-if="activity_logs.links?.length" :links="activity_logs.links" />
        </div>
    </div>
    </div>
</template>
